import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "motion/react";
import { Layers, Terminal, Sparkles } from "lucide-react";
import { useLanguage } from "./LanguageContext";

export default function About() {
  const { t } = useLanguage();
  const [index, setIndex] = useState(0);
  const words = t("words", "about") || [];

  useEffect(() => {
    if (words.length === 0) return;
    const interval = setInterval(() => {
      setIndex((prevIndex) => (prevIndex + 1) % words.length);
    }, 2800);
    return () => clearInterval(interval);
  }, [words.length]);

  return (
    <section id="philosophy" className="relative py-24 md:py-36 bg-[#FAFAFC] overflow-hidden">
      {/* Light Mesh Overlay background */}
      <div className="absolute inset-0 mesh-bg opacity-70 pointer-events-none" />
      
      {/* Subtle background glow */}
      <div className="absolute top-1/2 left-10 -translate-y-1/2 w-80 h-80 bg-indigo-50 rounded-full blur-3xl opacity-60 pointer-events-none" />

      <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
        
        {/* Aesthetic pairing of small mono title & display statement */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
          <div className="lg:col-span-4 flex flex-col gap-3">
            <span className="text-[10px] font-mono tracking-widest text-indigo-600 uppercase flex items-center gap-2">
              <span className="w-1.5 h-1.5 bg-indigo-600 rounded-full animate-ping" /> {t("philosophyTag", "about")}
            </span>
            <h2 className="font-display text-2xl lg:text-3xl font-bold text-brand-charcoal tracking-tight">
              {t("header", "about")}
            </h2>
          </div>

          <div className="lg:col-span-8 flex flex-col gap-10">
            {/* Monumental display quote */}
            <h3 className="font-display text-3xl sm:text-5xl md:text-6xl text-[#111827] leading-[1.12] tracking-tight font-light text-left">
              {t("quotePart1", "about")}<strong className="font-medium text-black">{t("quoteSimplicity", "about")}</strong>{t("quotePart2", "about")}<span className="italic text-indigo-600 font-normal">{t("quotePrecision", "about")}</span>{t("quotePart3", "about")}<strong className="font-medium">{t("quotePurpose", "about")}</strong>{t("quotePart4", "about")}
            </h3>

            {/* Keyword kinetic morphing widget */}
            <div className="pt-8 border-t border-gray-150 flex flex-col sm:flex-row sm:items-center gap-6">
              <span className="text-xs font-mono text-gray-400 tracking-widest uppercase">
                {t("specializations", "about")}
              </span>
              <div className="h-10 flex items-center overflow-hidden">
                <AnimatePresence mode="wait">
                  {words[index] && (
                    <motion.div
                      key={words[index]}
                      initial={{ y: 20, opacity: 0, filter: "blur(4px)" }}
                      animate={{ y: 0, opacity: 1, filter: "blur(0px)" }}
                      exit={{ y: -20, opacity: 0, filter: "blur(4px)" }}
                      transition={{ duration: 0.65, ease: [0.16, 1, 0.3, 1] }}
                      className="text-xl sm:text-2xl font-mono text-indigo-600 font-medium tracking-wide flex items-center gap-3.5"
                    >
                      <Sparkles size={18} className="text-indigo-400 animate-spin-slow" />
                      {words[index]}
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </div>
          </div>
        </div>

        {/* Bento grid points of core beliefs */}
        <div id="about" className="grid grid-cols-1 md:grid-cols-3 gap-6 pt-20 md:pt-28">
          <motion.div 
            whileHover={{ y: -4 }}
            className="p-8 rounded-2xl glass-card flex flex-col gap-5 hover:shadow-xl transition-all text-left"
          >
            <div className="w-10 h-10 rounded-xl bg-indigo-50 flex items-center justify-center text-indigo-600">
              <Terminal size={18} />
            </div>
            <h4 className="font-display font-semibold text-lg text-brand-charcoal">{t("bento1Title", "about")}</h4>
            <p className="text-gray-500 text-sm leading-relaxed font-light">
              {t("bento1Desc", "about")}
            </p>
          </motion.div>

          <motion.div 
            whileHover={{ y: -4 }}
            className="p-8 rounded-2xl glass-card flex flex-col gap-5 hover:shadow-xl transition-all text-left"
            style={{ transitionDelay: "0.1s" }}
          >
            <div className="w-10 h-10 rounded-xl bg-indigo-50 flex items-center justify-center text-indigo-600">
              <Layers size={18} />
            </div>
            <h4 className="font-display font-semibold text-lg text-brand-charcoal">{t("bento2Title", "about")}</h4>
            <p className="text-gray-500 text-sm leading-relaxed font-light">
              {t("bento2Desc", "about")}
            </p>
          </motion.div>

          <motion.div 
            whileHover={{ y: -4 }}
            className="p-8 rounded-2xl glass-card flex flex-col gap-5 hover:shadow-xl transition-all text-left"
            style={{ transitionDelay: "0.2s" }}
          >
            <div className="w-10 h-10 rounded-xl bg-indigo-50 flex items-center justify-center text-indigo-600">
              <Sparkles size={18} />
            </div>
            <h4 className="font-display font-semibold text-lg text-brand-charcoal">{t("bento3Title", "about")}</h4>
            <p className="text-gray-500 text-sm leading-relaxed font-light">
              {t("bento3Desc", "about")}
            </p>
          </motion.div>
        </div>

      </div>
    </section>
  );
}
